import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Users } from "lucide-react";
import { Button } from "@/components/form/Button";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import Loader from "@/components/loaders/Loader";
import { useGetBrands } from "@/hooks/useGetBrands";
import { usePermissions } from "@/hooks/usePermissions";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useCalendarViewStore } from "@/stores/calendarViewStore";
import type { UpcomingPost } from "../content-calendar/api/useScheduling";
import type {
  BrandCalendarSettings,
  CalendarMediaType,
  CalendarMonthPlan,
  CalendarMonthPlanListRow,
  CalendarOrientation,
  CalendarPlanSlot,
  CalendarSlotStatus,
  CalendarVideoLength,
} from "@/types/contentCalendar.type";
import type { BriefRailInfo } from "@/types/productionBrief.type";
import {
  hasBriefStageSlots,
  isPlanFrozen,
} from "@/types/contentCalendar.type";
import {
  useAddPlanSlot,
  useCreateDirectSlot,
  useCalendarPillars,
  useCalendarPlan,
  useCalendarPlans,
  useCalendarSettings,
  useCancelPlanning,
  useClientNotifyState,
  useDeletePlanSlot,
  useGeneratePlan,
  useMovePlanSlot,
  useDecidePostRisk,
  useRecallSlotFromClient,
  useSendPosts,
  useSetPlanningGuidance,
  useUpdateCalendarSettings,
  useUpdateMonthCadence,
  isNotPlanning,
} from "@/app/routes/dashboard/content-calendar/api/useContentCalendar";
import PlanAskCard from "@/app/routes/dashboard/content-calendar/month/PlanAskCard";
import {
  briefBySlot,
  usePlanBriefs,
} from "@/app/routes/dashboard/content-calendar/api/useProductionBrief";
import {
  addMonths,
  isMonthInPast,
  monthLabel,
  toUtcIsoDay,
} from "@/app/routes/dashboard/content-calendar/calendar.utils";
import { statusAfterMove } from "@/app/routes/dashboard/content-calendar/slotStage";
import type { CalendarRow } from "@/app/routes/dashboard/content-calendar/calendar-index/calendarIndex";
import type { NewPost } from "@/app/routes/dashboard/content-calendar/month/MonthGrid";
import MonthView, { type MonthState } from "@/app/routes/dashboard/content-calendar/month/MonthView";
import { useOtherMonths } from "@/app/routes/dashboard/content-calendar/api/useOtherMonths";
import { useSlotIdeation } from "@/app/routes/dashboard/content-calendar/api/useSlotIdeation";
import { dayMonthShort } from "@/app/routes/dashboard/content-calendar/month/calendarModel";
import type { CadenceDraft } from "@/app/routes/dashboard/content-calendar/month/CadenceDialog";
import RegenerateMonthModal from "@/app/routes/dashboard/content-calendar/components/RegenerateMonthModal";
import { useCalendarViewport } from "@/app/routes/dashboard/content-calendar/month/CalendarSheet";
import {
  stageActionLabel,
  stageDestination,
} from "@/app/routes/dashboard/content-calendar/month/contentStage";
import QuickPostModal from "@/app/routes/dashboard/content-calendar/components/QuickPostModal";
import BrandGuestsModal from "@/app/routes/dashboard/content-calendar/components/BrandGuestsModal";
import SendToClientModal from "@/app/routes/dashboard/content-calendar/components/SendToClientModal";
import ConceptsView from "@/app/routes/dashboard/content-calendar/concepts/ConceptsView";
import ClientProposalsView from "@/app/routes/dashboard/content-calendar/review/ClientProposalsView";
import DeliveredView from "@/app/routes/dashboard/content-calendar/delivered/DeliveredView";
import ClientPreviewView from "@/app/routes/dashboard/content-calendar/client-window/ClientPreviewView";
import {
  useBrandMemories,
  useDeleteBrandMemory,
} from "@/app/routes/dashboard/settings/api/useBrandMemories";
import CalendarIndexView from "@/app/routes/dashboard/content-calendar/calendar-index/CalendarIndexView";
import {
  windowKeys,
  type YearWindowStart,
} from "@/app/routes/dashboard/content-calendar/calendar-index/CalendarYearGrid";
import { agentRail, useAgentFocus } from "@/app/routes/dashboard/agent/agentFocusStore";
import { EmptyState } from "../components/ViewStates";
import { queue } from "@/components/Toast";
import {
  loadMoveHistory,
  saveMoveHistory,
  type CalendarMoveRecord,
} from "@/app/routes/dashboard/content-calendar/month/moveHistory";

/** Pipeline posts live in Concepts. `post` remains only for ready-made Quick Posts. */
type Surface = "month" | "concepts" | "review" | "client-preview" | "delivered" | "post";

/** The rail card with Creo's two questions while a month plans. */
const PLAN_ASK_KEY = "calendar:plan-ask";

function validYearMonth(year: number, month: number) {
  return Number.isInteger(year) && year >= 2000 && year <= 2100 && Number.isInteger(month) && month >= 1 && month <= 12;
}

function lastActionFor(plan: CalendarMonthPlan): string | null {
  if (plan.generationError && plan.status === "DRAFT") return "Planning failed";
  if (plan.status === "GENERATING") return "Planning started";
  if (plan.status === "ARCHIVED") return "Month archived";
  if (plan.status === "APPROVED") return `Planned ${plan.targetSlotCount} posts`;
  return null;
}

function rowFromPlan(plan: CalendarMonthPlanListRow): CalendarRow {
  return {
    planId: plan.id,
    year: plan.year,
    month: plan.month,
    planStatus: plan.status,
    targets: {
      static: plan.targetStaticCount,
      vertical: plan.targetVerticalVideoCount,
      horizontal: plan.targetHorizontalVideoCount,
    },
    generationError: plan.generationError,
    approvedAt: plan.approvedAt,
    slotCounts: plan.slotCounts,
    lastActionAt: plan.updatedAt,
    lastAction: lastActionFor(plan),
    // The list endpoint carries the approving user id but not their display
    // name. Omit the byline instead of turning an id into interface copy.
    signedBy: null,
  };
}

function nextUnplannedRow(plans: CalendarMonthPlanListRow[]): CalendarRow {
  const today = new Date();
  const current = { year: today.getUTCFullYear(), month: today.getUTCMonth() + 1 };
  const newest = plans.reduce(
    (latest, plan) =>
      plan.year > latest.year || (plan.year === latest.year && plan.month > latest.month)
        ? { year: plan.year, month: plan.month }
        : latest,
    current,
  );
  const next = addMonths(newest, 1);
  return {
    planId: null,
    ...next,
    planStatus: null,
    targets: null,
    generationError: null,
    approvedAt: null,
    slotCounts: null,
    lastActionAt: null,
    lastAction: null,
    signedBy: null,
  };
}

function CalendarBreadcrumb({
  year,
  month,
  surface,
  onIndex,
  onMonth,
}: {
  year: number;
  month: number;
  surface: Surface;
  onIndex: () => void;
  onMonth: () => void;
}) {
  return (
    <nav aria-label="Where you are" className="flex flex-wrap items-center gap-x-2 gap-y-1 pb-4">
      <button
        type="button"
        onClick={onIndex}
        className="tt-label uppercase text-co-muted underline decoration-co-line2 underline-offset-4 transition-colors hover:text-co-text hover:decoration-co-text"
      >
        Content calendar
      </button>
      <span aria-hidden="true" className="tt-label text-co-faint">/</span>
      {surface === "month" || surface === "delivered" ? (
        <span className="tt-label uppercase text-co-faint">{monthLabel({ year, month })}</span>
      ) : (
        <>
          <button
            type="button"
            onClick={onMonth}
            className="tt-label uppercase text-co-muted underline decoration-co-line2 underline-offset-4 transition-colors hover:text-co-text hover:decoration-co-text"
          >
            {monthLabel({ year, month })}
          </button>
          <span aria-hidden="true" className="tt-label text-co-faint">/</span>
          <span className="tt-label uppercase text-co-faint">
            {surface === "concepts"
              ? "Concepts"
              : surface === "post"
                ? "Quick post"
                : "Client proposals"}
          </span>
        </>
      )}
    </nav>
  );
}

function CalendarLoading({ label = "Loading calendar…" }: { label?: string }) {
  return (
    <div className="grid min-h-[60vh] place-items-center">
      <div className="flex flex-col items-center gap-3">
        <Loader />
        <p className="tt-read-s text-co-faint">{label}</p>
      </div>
    </div>
  );
}

function CalendarError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="grid min-h-[60vh] place-items-center px-6">
      <div className="flex max-w-[28rem] flex-col items-center gap-3 text-center">
        <h1 className="font-section text-[24px] font-bold text-co-text">The calendar could not be loaded</h1>
        <p className="text-[15px] text-co-text2">The latest calendar data did not arrive. Nothing you did was lost.</p>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex h-8 items-center bg-co-accent-fill px-3 text-[13.5px] font-medium text-co-on-accent"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function LiveCalendarIndex() {
  const [, setSearchParams] = useSearchParams();
  const { selectedBrandId } = useWorkspaceStore();
  const plansQuery = useCalendarPlans(selectedBrandId);
  const settingsQuery = useCalendarSettings(selectedBrandId);
  const pillarsQuery = useCalendarPillars(selectedBrandId);
  const { canEdit, isOwner } = usePermissions();
  // Brand memory is Owner-only; everyone else never asks for it.
  const memoriesQuery = useBrandMemories(selectedBrandId, { enabled: isOwner });
  // The brand's cadence is edited HERE, on the index; a month's own is edited
  // on that month. Two scopes of the same three numbers, each on its own page.
  const updateSettings = useUpdateCalendarSettings(selectedBrandId);
  const brandsQuery = useGetBrands();
  const clientName = brandsQuery.data?.find((brand) => brand.id === selectedBrandId)?.name ?? null;
  // The year draws each month's days, so it reads the twelve months on screen and nothing else.
  const [windowStart, setWindowStart] = useState<YearWindowStart | null>(null);
  const yearWindow = useMemo(() => (windowStart ? windowKeys(windowStart) : []), [windowStart]);
  const monthsRead = useOtherMonths(plansQuery.data, { enabled: true, only: yearWindow, withBriefs: true });
  const rows = useMemo(() => {
    if (!plansQuery.data) return [];
    const byPlan = new Map<string, CalendarPlanSlot[]>();
    for (const slot of monthsRead.slots) {
      const list = byPlan.get(slot.planId);
      if (list) list.push(slot);
      else byPlan.set(slot.planId, [slot]);
    }
    return [
      ...plansQuery.data.map((plan) => {
        const row = rowFromPlan(plan);
        const slots = byPlan.get(plan.id);
        return slots ? { ...row, slots, briefBySlot: monthsRead.briefBySlot } : row;
      }),
      nextUnplannedRow(plansQuery.data),
    ];
  }, [plansQuery.data, monthsRead.slots, monthsRead.briefBySlot]);

  const openMonth = useCallback(
    (row: CalendarRow, opts?: { plan?: boolean }) => {
      // The year opens a month on Month. Week, List and Schedule stay where they were left inside a month.
      useCalendarViewStore.getState().setLayoutMode("month");
      const next = new URLSearchParams({ year: String(row.year), month: String(row.month) });
      if (row.planStatus === "ARCHIVED") next.set("view", "delivered");
      // Plan {Month} on a tile: the month opens and starts its plan at once (Figma 08, 13 Oct).
      if (opts?.plan) next.set("plan", "1");
      setSearchParams(next);
    },
    [setSearchParams],
  );

  useAgentFocus({ surface: "calendar", view: "index" });

  if (!selectedBrandId) {
    return <EmptyState title="No brand selected" body="Pick a brand to open its content calendars." />;
  }

  return (
    <CalendarIndexView
      brandId={selectedBrandId}
      rows={rows}
      settings={settingsQuery.data ?? null}
      pillarCount={pillarsQuery.data?.pillars.length ?? null}
      memoryCount={memoriesQuery.data?.memories.length ?? null}
      pillars={pillarsQuery.data?.pillars}
      clientName={clientName}
      onWindowChange={setWindowStart}
      isLoading={plansQuery.isLoading || settingsQuery.isLoading}
      isError={plansQuery.isError || settingsQuery.isError}
      onOpenMonth={openMonth}
      savingCadence={updateSettings.isPending}
      onSaveCadence={
        canEdit
          ? async (draft) => {
              await updateSettings.mutateAsync(draft);
            }
          : undefined
      }
    />
  );
}

function LiveCalendarDetail({ year, month }: { year: number; month: number }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedBrandId } = useWorkspaceStore();
  const { canEdit, isOwner, isGuest } = usePermissions();
  const viewport = useCalendarViewport();
  const navigate = useNavigate();
  const brandsQuery = useGetBrands();
  const setStoredMonth = useCalendarViewStore((state) => state.setView);
  const plansQuery = useCalendarPlans(selectedBrandId);
  const settingsQuery = useCalendarSettings(selectedBrandId);
  const pillarsQuery = useCalendarPillars(selectedBrandId);
  const memoriesQuery = useBrandMemories(selectedBrandId, { enabled: isOwner });
  const [shareOpen, setShareOpen] = useState(false);
  const [sendingSlotId, setSendingSlotId] = useState<string | null>(null);
  const [quickPostDay, setQuickPostDay] = useState<{
    year: number;
    month: number;
    day: number;
  } | null>(null);
  /* Open while the re-plan confirmation is up. `cadence` is what the editor
     had on screen when it differed from the month's saved numbers. */
  const [replan, setReplan] = useState<{ cadence: CadenceDraft | null } | null>(null);

  const listedPlan = useMemo(
    () => plansQuery.data?.find((plan) => plan.year === year && plan.month === month) ?? null,
    [plansQuery.data, year, month],
  );
  const planQuery = useCalendarPlan(listedPlan?.id ?? null);
  const plan = planQuery.data ?? null;
  const planId = listedPlan?.id ?? null;
  const slots = useMemo(() => plan?.slots ?? [], [plan?.slots]);
  const slotsRef = useRef(slots);
  const [moveHistoryByBrand, setMoveHistoryByBrand] = useState<
    Record<string, CalendarMoveRecord[]>
  >({});
  const planStatus = plan?.status ?? listedPlan?.status ?? "NONE";
  // A guest's client-side page for this month (see content-calendar/guest).
  const contentReviewHref = `/dashboard/content-review${planId ? `?planId=${planId}` : ""}`;
  const surfaceParam = searchParams.get("view");
  const slotId = searchParams.get("slot");
  // A slot with no view (the agent's Navigate links) opens that post.
  const surface: Surface =
    surfaceParam === "client-preview"
      ? "client-preview"
      : (surfaceParam === "post" || surfaceParam === "concepts" || !surfaceParam) && slotId
        ? "concepts"
        : surfaceParam === "concepts"
          ? "concepts"
          : surfaceParam === "review"
            ? "review"
            : surfaceParam === "delivered" || planStatus === "ARCHIVED"
              ? "delivered"
              : "month";

  useEffect(() => setStoredMonth({ year, month }), [setStoredMonth, year, month]);
  useEffect(() => {
    slotsRef.current = slots;
  }, [slots]);

  const ideation = useSlotIdeation(planId);
  const calendarView = useCalendarViewStore((state) => state.layoutMode);
  const setCalendarView = useCalendarViewStore((state) => state.setLayoutMode);
  const monthKey = (y: number, m: number) => `${y}-${String(m).padStart(2, "0")}`;
  const neighbourKeys = useMemo(() => {
    const before = addMonths({ year, month }, -1);
    const after = addMonths({ year, month }, 1);
    return [monthKey(before.year, before.month), monthKey(after.year, after.month)];
  }, [year, month]);
  // The week's days outside this month, and the Needs you queue across months.
  const neighbours = useOtherMonths(plansQuery.data, {
    enabled: calendarView === "week",
    excludePlanId: planId,
    only: neighbourKeys,
  });
  // Other months are read only when the month asks: the Needs you queue while
  // List has Needs you on, and a phone's schedule, which runs across months.
  // Twelve months either side of the open one.
  const [queueWanted, setQueueWanted] = useState(false);
  const queueWindow = useMemo(
    () =>
      Array.from({ length: 25 }, (_, index) => {
        const at = addMonths({ year, month }, index - 12);
        return monthKey(at.year, at.month);
      }),
    [year, month],
  );
  const needsQueue = useOtherMonths(plansQuery.data, {
    enabled: queueWanted && !isGuest,
    excludePlanId: planId,
    only: queueWindow,
    withBriefs: true,
  });
  const monthsWithPosts = useMemo(
    () =>
      new Set(
        (plansQuery.data ?? [])
          .filter((row) => Object.values(row.slotCounts ?? {}).some((n) => (n ?? 0) > 0))
          .map((row) => monthKey(row.year, row.month)),
      ),
    [plansQuery.data],
  );
  const selectedBrandName =
    brandsQuery.data?.find((brand) => brand.id === selectedBrandId)?.name ?? "Brand";
  const recallSlot = useRecallSlotFromClient(selectedBrandId, planId, {
    clientName: selectedBrandName,
    // Undo sends it again as it was, without a second email.
    onUndo: (id) => sendPosts.mutate({ slotIds: [id], notify: false }),
  });
  const moveSlot = useMovePlanSlot(selectedBrandId, planId);
  const decideRisk = useDecidePostRisk(selectedBrandId, planId);
  const addSlot = useAddPlanSlot(selectedBrandId, planId);
  const createDirectSlot = useCreateDirectSlot(selectedBrandId);
  const deleteSlot = useDeletePlanSlot(selectedBrandId, planId);
  const sendPosts = useSendPosts(selectedBrandId, planId);
  // THIS month's cadence only. The brand's is edited on the index.
  const updateMonthCadence = useUpdateMonthCadence(selectedBrandId);
  const generatePlan = useGeneratePlan(selectedBrandId);
  const cancelPlanning = useCancelPlanning(selectedBrandId, planId);
  const setPlanningGuidance = useSetPlanningGuidance();
  const deleteMemory = useDeleteBrandMemory(selectedBrandId);
  const briefsEnabled = hasBriefStageSlots(slots);
  const briefsQuery = usePlanBriefs(planId, briefsEnabled);
  const briefs = briefBySlot(briefsQuery.data);
  const selectedSlot = slotId ? slots.find((slot) => slot.id === slotId) ?? null : null;
  const sendingSlot = slots.find(slot => slot.id === sendingSlotId) ?? null;
  // Read through the notify preflight rather than the guest list: every team
  // role can see the "nobody to review this" warning, and the send dialog
  // reuses the same query.
  // Team-only preflight: a guest (the brand's client) is the reviewer, not the sender.
  const notifyState = useClientNotifyState(planId, !!listedPlan && slots.length > 0 && !isGuest);
  const nobodyToReview =
    !!notifyState.data && notifyState.data.guests.length === 0 && slots.length > 0;

  const goIndex = useCallback(() => setSearchParams(new URLSearchParams()), [setSearchParams]);
  const goMonth = useCallback(
    () => setSearchParams(new URLSearchParams({ year: String(year), month: String(month) })),
    [month, setSearchParams, year],
  );
  const goToMonth = useCallback(
    (nextYear: number, nextMonth: number) =>
      setSearchParams(new URLSearchParams({ year: String(nextYear), month: String(nextMonth) })),
    [setSearchParams],
  );
  const openConcept = useCallback(
    (nextSlotId?: string) => {
      const slot = nextSlotId ? slotsRef.current.find((item) => item.id === nextSlotId) : undefined;
      const next = new URLSearchParams({
        year: String(year),
        month: String(month),
        view: "concepts",
      });
      if (nextSlotId) next.set("slot", nextSlotId);
      if (slot?.origin === "DIRECT") next.set("phase", "publishing");
      setSearchParams(next);
    },
    [month, setSearchParams, year],
  );
  const openPublishing = useCallback(
    (slot: CalendarPlanSlot) => {
      setSearchParams(
        new URLSearchParams({
          year: String(year),
          month: String(month),
          view: "concepts",
          slot: slot.id,
          phase: "publishing",
        }),
      );
    },
    [month, setSearchParams, year],
  );
  // Schedule: a post going live opens on its publishing step, in its own month.
  const openScheduled = useCallback(
    (post: UpcomingPost) => {
      const day = new Date(post.slot.scheduledAt);
      const next = new URLSearchParams({
        year: String(day.getUTCFullYear()),
        month: String(day.getUTCMonth() + 1),
        view: "concepts",
        slot: post.slot.id,
        phase: "publishing",
      });
      setSearchParams(next);
    },
    [setSearchParams],
  );
  // "Pick ideas for 3 posts": the first post waiting for a pick, with the walk
  // on, so each pick moves the page on to the next (ConceptsView, ?walk=ideas).
  const openIdeasWalk = useCallback(
    (slot: CalendarPlanSlot) => {
      setSearchParams(
        new URLSearchParams({ year: String(year), month: String(month), view: "concepts", slot: slot.id, walk: "ideas" }),
      );
    },
    [month, setSearchParams, year],
  );
  const openMonthSlot = useCallback(
    (slot: CalendarPlanSlot) => {
      const destination = stageDestination(slot, briefs.get(slot.id));
      const on = new Date(slot.scheduledAt);
      const slotYear = on.getUTCFullYear();
      const slotMonth = on.getUTCMonth() + 1;
      if (slotYear !== year || slotMonth !== month) {
        // A queue post from another month opens on that month's post page.
        const next = new URLSearchParams({
          year: String(slotYear),
          month: String(slotMonth),
          view: "concepts",
          slot: slot.id,
        });
        if (destination === "brief") next.set("phase", "brief");
        else if (destination === "publishing") next.set("phase", "publishing");
        setSearchParams(next);
        return;
      }
      if (destination === "brief") {
        setSearchParams(
          new URLSearchParams({
            year: String(year),
            month: String(month),
            view: "concepts",
            slot: slot.id,
            phase: "brief",
          }),
        );
        return;
      }
      if (destination === "publishing") {
        openPublishing(slot);
        return;
      }
      openConcept(slot.id);
    },
    [briefs, month, openConcept, openPublishing, setSearchParams, year],
  );
  const openClientPreview = useCallback(
    (nextSlotId: string | null, returnTo: "concepts" | "review") => {
      const next = new URLSearchParams({
        year: String(year),
        month: String(month),
        view: "client-preview",
        return: returnTo,
      });
      if (nextSlotId) next.set("slot", nextSlotId);
      setSearchParams(next);
    },
    [month, setSearchParams, year],
  );
  const closeClientPreview = useCallback(() => {
    const returnTo = searchParams.get("return") === "review" ? "review" : "concepts";
    const next = new URLSearchParams({
      year: String(year),
      month: String(month),
      view: returnTo,
    });
    if (returnTo === "concepts" && slotId) next.set("slot", slotId);
    setSearchParams(next);
  }, [month, searchParams, setSearchParams, slotId, year]);
  const addMoveRecord = useCallback(
    (record: CalendarMoveRecord) => {
      if (!selectedBrandId) return;
      setMoveHistoryByBrand((previousByBrand) => {
        const previous = previousByBrand[selectedBrandId] ?? loadMoveHistory(selectedBrandId);
        const next = [record, ...previous].slice(0, 20);
        return { ...previousByBrand, [selectedBrandId]: next };
      });
    },
    [selectedBrandId],
  );

  const markMoveUndone = useCallback(
    (recordId: string) => {
      if (!selectedBrandId) return;
      setMoveHistoryByBrand((previousByBrand) => {
        const previous = previousByBrand[selectedBrandId] ?? loadMoveHistory(selectedBrandId);
        const next = previous.map((record) =>
          record.id === recordId
            ? { ...record, undoneAt: new Date().toISOString() }
            : record,
        );
        return { ...previousByBrand, [selectedBrandId]: next };
      });
    },
    [selectedBrandId],
  );

  useEffect(() => {
    if (!selectedBrandId || !moveHistoryByBrand[selectedBrandId]) return;
    saveMoveHistory(selectedBrandId, moveHistoryByBrand[selectedBrandId]);
  }, [moveHistoryByBrand, selectedBrandId]);

  const undoMove = useCallback(
    async (record: CalendarMoveRecord) => {
      const current = slotsRef.current.find((slot) => slot.id === record.slotId);
      if (!current || record.planId !== planId || new Date(current.scheduledAt).getUTCDate() !== record.toDay) {
        queue.add({
          title: "This move cannot be undone now",
          description: "The post has changed since that move. Open it to review its current schedule.",
          variant: "info",
        });
        return;
      }
      try {
        await moveSlot.mutateAsync({
          slotId: record.slotId,
          scheduledAt: toUtcIsoDay(record.year, record.month, record.fromDay),
          position: record.fromPosition,
          status: record.fromStatus,
        });
        markMoveUndone(record.id);
        queue.add({ title: "Move undone", description: `${record.title} is back on day ${record.fromDay}.`, variant: "success" });
      } catch {
        // useMovePlanSlot restores the snapshot and names the server problem.
      }
    },
    [markMoveUndone, moveSlot, planId],
  );

  const moveCalendarSlot = useCallback(
    async ({ slotId: movingId, day, position }: { slotId: string; day: number; position: number }) => {
      if (!selectedBrandId || !planId) return;
      const moving = slotsRef.current.find((slot) => slot.id === movingId);
      if (!moving) return;
      const fromDay = new Date(moving.scheduledAt).getUTCDate();
      const fromPosition = [...slotsRef.current]
        .filter((slot) => new Date(slot.scheduledAt).getUTCDate() === fromDay)
        .sort((a, b) => a.order - b.order)
        .findIndex((slot) => slot.id === movingId);
      if (fromDay === day && fromPosition === position) return;
      const toStatus = statusAfterMove(moving.status, { year, month, day }) as CalendarSlotStatus;
      const record: CalendarMoveRecord = {
        id: `${Date.now()}-${moving.id}`,
        brandId: selectedBrandId,
        planId,
        year,
        month,
        slotId: moving.id,
        title: moving.title,
        fromDay,
        toDay: day,
        fromPosition: Math.max(0, fromPosition),
        toPosition: position,
        fromStatus: moving.status,
        toStatus,
        createdAt: new Date().toISOString(),
        undoneAt: null,
      };

      await moveSlot.mutateAsync({
        slotId: moving.id,
        scheduledAt: toUtcIsoDay(year, month, day),
        position,
        status: toStatus,
      });
      addMoveRecord(record);
      const landed = new Date(Date.UTC(year, month - 1, day));
      queue.add(
        {
          title: `Moved to ${dayMonthShort({ weekday: landed.getUTCDay(), day, month })}`,
          variant: "success",
          action: { label: "Undo", onPress: () => void undoMove(record) },
        },
        { timeout: 8000 },
      );
    },
    [addMoveRecord, month, moveSlot, planId, selectedBrandId, undoMove, year],
  );

  const isPastMonth = isMonthInPast(year, month);
  const planEditable = ["DRAFT", "PENDING_APPROVAL", "GENERATING"].includes(planStatus);
  const generating = planStatus === "GENERATING";
  const canPlan = canEdit && !isPastMonth && (!listedPlan || planEditable);
  // Re-plan needs a month that was planned and is still live. The server only
  // refuses ARCHIVED; a run already going is left to finish.
  const canReplan =
    canEdit && !!plan && !isPastMonth && planStatus !== "ARCHIVED" && !generating;
  const replanCadence = replan?.cadence ?? null;

  /* Where the user is standing, for the shell rail. The agent picks its
     calendar tools from this, and the tool-invalidation map refreshes whatever
     a write touched — so the page no longer carries refresh callbacks of its
     own. "concepts" and "delivered" are month-scoped surfaces the focus type
     does not name, so they report the month they belong to. */
  useAgentFocus({
    surface: "calendar",
    ...(planId ? { planId } : {}),
    year,
    month,
    ...(selectedSlot ? { slotId: selectedSlot.id } : {}),
    view: surface === "review" ? surface : "month",
  });

  // Plan {Month} (Figma 08, 13 Oct): the run starts at once, no card, no
  // confirm; the month says "Planning January · 0 of 5 placed · 0:04." and
  // Creo's two questions wait in the side chat. The answer is the month's
  // planning note; once the month has landed it goes to Creo to apply instead.
  const startPlan = useCallback(() => {
    const monthName = new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
    const client = selectedBrandName === "Brand" ? "the brand" : selectedBrandName;
    generatePlan.mutate(
      { year, month, quiet: true },
      {
        onSuccess: (started) => {
          agentRail.ask({
            key: PLAN_ASK_KEY,
            prompt: `Plan ${monthName}`,
            card: (done) => (
              <PlanAskCard
                monthName={monthName}
                clientName={client}
                onDone={done}
                onAnswer={async (text) => {
                  try {
                    await setPlanningGuidance.mutateAsync({ planId: started.id, text });
                    return `Noted. It shapes ${monthName}’s ideas as they land. I’ll go over the posts with you once the month is in.`;
                  } catch (err) {
                    if (!isNotPlanning(err)) throw err;
                    agentRail.send(`${monthName} just landed. My note while it planned: ${text}. Apply it to the posts and tell me what changed.`);
                    return `${monthName} has already landed, so I’m applying it to the posts now.`;
                  }
                }}
              />
            ),
          });
        },
      },
    );
  }, [generatePlan, month, selectedBrandName, setPlanningGuidance, year]);
  useEffect(() => () => agentRail.clearAsk(PLAN_ASK_KEY), []);
  // The year's tile opened this month with ?plan=1: start once, then drop the flag.
  const planParam = searchParams.get("plan") === "1";
  const planStarted = useRef(false);
  const canPlanNow = canPlan && !generating;
  useEffect(() => {
    if (!planParam || planStarted.current || !canPlanNow || plansQuery.isLoading) return;
    planStarted.current = true;
    startPlan();
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("plan");
        return next;
      },
      { replace: true },
    );
  }, [canPlanNow, planParam, plansQuery.isLoading, setSearchParams, startPlan]);

  if (!selectedBrandId) {
    return <EmptyState title="No brand selected" body="Pick a brand to open its content calendars." />;
  }
  if (plansQuery.isLoading || settingsQuery.isLoading || pillarsQuery.isLoading) {
    // The month keeps its header while the calendar reads: a skeleton grid, not a spinner.
    if (!surfaceParam && !slotId) {
      return (
        <MonthView
          year={year}
          month={month}
          state="loading"
          slots={[]}
          targets={null}
          view={calendarView}
          onViewChange={setCalendarView}
          onOpenSlot={() => undefined}
          onPrevMonth={() => {
            const previous = addMonths({ year, month }, -1);
            goToMonth(previous.year, previous.month);
          }}
          onNextMonth={() => {
            const next = addMonths({ year, month }, 1);
            goToMonth(next.year, next.month);
          }}
          onPickMonth={goToMonth}
          onSeeYear={goIndex}
        />
      );
    }
    return <CalendarLoading />;
  }
  if (plansQuery.isError || settingsQuery.isError || pillarsQuery.isError) {
    return <CalendarError onRetry={() => { void plansQuery.refetch(); void settingsQuery.refetch(); void pillarsQuery.refetch(); }} />;
  }
  if (listedPlan && planQuery.isError && surface !== "month") {
    return <CalendarError onRetry={() => { void planQuery.refetch(); }} />;
  }

  const monthLoading = Boolean(listedPlan && planQuery.isLoading);

  const crumb = (
    <CalendarBreadcrumb year={year} month={month} surface={surface} onIndex={goIndex} onMonth={goMonth} />
  );

  // Rendered alongside every surface: both are month-level, and losing them
  // with the old route is what took Quick Post and client management out of
  // the app entirely.
  const modals = (
    <>
      {sendingSlot && planId ? <SendToClientModal
        key={sendingSlot.id}
        isOpen
        planId={planId}
        // The concept and its creative brief go together, answered once (Figma 08).
        kind="post"
        items={sendablePosts(slots, briefs).map((slot) => ({
          slotId: slot.id,
          scheduledAt: slot.scheduledAt,
          title: slot.ideas?.find((idea) => idea.status === "ACCEPTED")?.title ?? slot.title,
          meta: "Concept and brief",
          versionLabel: `version ${(briefs.get(slot.id)?.sentVersion ?? 0) + 1}`,
        }))}
        replyByDaysBefore={settingsQuery.data?.replyByDaysBefore ?? null}
        busy={sendPosts.isPending}
        onPublish={async (ids, options) => {
          const result = await sendPosts.mutateAsync({ slotIds: ids, ...options });
          const n = ids.length;
          queue.add({
            title: `Sent to ${selectedBrandName}`,
            description: n === 1 ? "The idea and its brief are on their page." : `${n} posts, each idea with its brief, are on their page.`,
            variant: "success",
            action: { label: "Undo", onPress: () => ids.forEach((id) => recallSlot.mutate(id)) },
            countdown: 6,
          });
          return result;
        }}
        onClose={() => setSendingSlotId(null)}
        onManageGuests={() => { setSendingSlotId(null); setShareOpen(true); }}
      /> : null}
      {quickPostDay && canEdit && (
        <QuickPostModal
          brandId={selectedBrandId}
          defaultDay={quickPostDay}
          onCreated={(createdSlotId, createdPlanId) => {
            // Land the operator on the new post — the media upload is the next
            // thing they want, and it lives in the workroom.
            if (createdPlanId === planId) {
              setSearchParams(
                new URLSearchParams({
                  year: String(year),
                  month: String(month),
                  view: "concepts",
                  slot: createdSlotId,
                  phase: "publishing",
                }),
              );
            }
          }}
          onClose={() => setQuickPostDay(null)}
        />
      )}

      {shareOpen && canEdit && (
        <BrandGuestsModal
          brandId={selectedBrandId}
          brandName={selectedBrandName === "Brand" ? undefined : selectedBrandName}
          canManage={isOwner}
          onClose={() => setShareOpen(false)}
        />
      )}

      {replan && plan && canReplan && (
        <RegenerateMonthModal
          brandId={selectedBrandId}
          planId={plan.id}
          planStatus={planStatus}
          targetYear={year}
          targetMonth={month}
          slots={slots}
          cadence={replanCadence}
          clientName={selectedBrandName === "Brand" ? null : selectedBrandName}
          briefBySlot={briefs}
          phone={viewport === "phone"}
          beforeReplan={
            replanCadence
              ? async () => {
                  await updateMonthCadence.mutateAsync({ year, month, ...replanCadence });
                }
              : undefined
          }
          onClose={() => setReplan(null)}
        />
      )}
    </>
  );

  // A guest IS the client: the team's send/review desk and its client preview
  // become the client's own Content review page.
  if (isGuest && (surface === "review" || surface === "client-preview")) {
    return <Navigate to={contentReviewHref} replace />;
  }
  // Every surface but the grid needs the month's plan. While it reads, hold the
  // loader the plans list showed rather than falling through to the month grid,
  // so a refresh on a post lands on the post instead of passing the calendar.
  if (monthLoading && surface !== "month") {
    return <CalendarLoading />;
  }

  if (surface === "client-preview") {
    return (
      <ClientPreviewView
        brandName={selectedBrandName}
        year={year}
        month={month}
        slot={selectedSlot}
        slots={slots}
        briefs={briefsQuery.data?.briefs ?? []}
        onClose={closeClientPreview}
      />
    );
  }

  if (surface === "concepts" && planId && plan) {
    // The post page opens on its date rail, whose month block is the way back.
    return (
      <div className="flex w-full flex-col">
        <ConceptsView
          brandId={selectedBrandId}
          plan={plan}
          planId={planId}
          year={year}
          month={month}
          slots={slots}
          pillars={pillarsQuery.data?.pillars}
          briefBySlot={briefs}
          selectedSlotId={slotId}
          onSelect={openConcept}
          onBack={goMonth}
          onPreviewAsClient={(id) => openClientPreview(id, "concepts")}
          onShare={setSendingSlotId}
          phase={searchParams.get("phase") === "publishing" ? "publishing" : searchParams.get("phase") === "distribution" ? "distribution" : searchParams.get("phase") === "analytics" ? "publishing" : searchParams.get("phase") === "upload" ? "upload" : searchParams.get("phase") === "brief" ? "brief" : "concept"}
          onPhaseChange={(phase) => setSearchParams(previous => {
            const next = new URLSearchParams(previous);
            if (phase !== "concept") next.set("phase", phase); else next.delete("phase");
            return next;
          })}
          onRecall={canEdit ? (id) => recallSlot.mutate(id) : undefined}
          recalling={recallSlot.isPending}
          onMoveSlot={
            canEdit && !isPlanFrozen(planStatus) && !generating ? moveCalendarSlot : undefined
          }
        />
        {modals}
      </div>
    );
  }

  if (surface === "review") {
    return (
      <div className="flex w-full flex-col">
        {crumb}
        {/* Figma 08, "Client proposals": everything that went to the client, or is ready to. */}
        <ClientProposalsView
          slots={slots}
          planId={planId}
          year={year}
          month={month}
          clientName={selectedBrandName}
          briefs={briefs}
          pillars={pillarsQuery.data?.pillars}
          canEdit={canEdit}
          onOpenPost={(slot, phase) => (phase === "publishing" ? openPublishing(slot) : openConcept(slot.id))}
          onSend={canEdit ? (slot) => setSendingSlotId(slot.id) : undefined}
          onBack={goMonth}
        />
        {modals}
      </div>
    );
  }

  if (surface === "delivered") {
    const snapshotSettings: BrandCalendarSettings | null = settingsQuery.data
      ? {
          ...settingsQuery.data,
          staticPostsPerMonth: listedPlan?.targetStaticCount ?? settingsQuery.data.staticPostsPerMonth,
          verticalVideoPostsPerMonth: listedPlan?.targetVerticalVideoCount ?? settingsQuery.data.verticalVideoPostsPerMonth,
          horizontalVideoPostsPerMonth: listedPlan?.targetHorizontalVideoCount ?? settingsQuery.data.horizontalVideoPostsPerMonth,
        }
      : null;
    return (
      <div className="flex w-full flex-col">
        {crumb}
        <DeliveredView
          year={year}
          month={month}
          slots={slots}
          settings={snapshotSettings}
          // Owner-only: everyone else gets no "what the next month carries" section.
          memories={isOwner ? (memoriesQuery.data?.memories ?? []) : undefined}
          signedBy={null}
          onForget={isOwner ? (id) => deleteMemory.mutate(id) : undefined}
          onPlanNext={goIndex}
          onOpenPost={openMonthSlot}
          openPostLabel={(slot) => (isGuest ? "Open post" : stageActionLabel(slot, briefs.get(slot.id)))}
        />
        {modals}
      </div>
    );
  }

  const targets = listedPlan
    ? {
        static: listedPlan.targetStaticCount,
        vertical: listedPlan.targetVerticalVideoCount,
        horizontal: listedPlan.targetHorizontalVideoCount,
      }
    : null;
  const canWriteMonth = canEdit && !!listedPlan && !isPlanFrozen(planStatus) && !generating;
  const noStrategy =
    !!pillarsQuery.data && !pillarsQuery.data.strategyId && pillarsQuery.data.pillars.length === 0;
  const planFailed =
    !generating && !!listedPlan && planStatus === "DRAFT" && !!listedPlan.generationError && slots.length === 0;
  const monthState: MonthState = monthLoading
    ? "loading"
    : listedPlan && planQuery.isError && !planQuery.data
      ? "error"
      : generating
        ? "planning"
        : planFailed
          ? "planning_failed"
          : slots.length === 0 && noStrategy
            ? "no_strategy"
            : !listedPlan || slots.length === 0
              ? "not_planned"
              : "ready";
  // Plan {Month} starts the run itself (startPlan); the chat only carries Creo's questions.
  const planThisMonth = canPlanNow ? startPlan : undefined;

  return (
    <div className="relative min-h-full" aria-busy={generating || monthLoading || undefined}>
      <MonthView
        year={year}
        month={month}
        state={monthState}
        slots={monthLoading ? [] : slots}
        neighbourSlots={calendarView === "week" ? neighbours.slots : []}
        queueSlots={queueWanted && !isGuest ? needsQueue.slots : []}
        queueLive={!isGuest}
        onQueueWanted={setQueueWanted}
        targets={targets}
        briefBySlot={
          !monthLoading
            ? new Map([
                ...(queueWanted ? needsQueue.briefBySlot : new Map()),
                ...(briefsEnabled ? briefs : new Map()),
              ])
            : undefined
        }
        brandId={selectedBrandId}
        clientName={selectedBrandName === "Brand" ? null : selectedBrandName}
        canEdit={canEdit && planStatus !== "ARCHIVED"}
        closed={isPastMonth}
        pillars={pillarsQuery.data?.pillars}
        view={calendarView}
        onViewChange={setCalendarView}
        monthsWithPosts={monthsWithPosts}
        onOpenSlot={openMonthSlot}
        onOpenScheduled={openScheduled}
        onPickIdeasWalk={canEdit && !isGuest ? openIdeasWalk : undefined}
        onOpenProposals={() => setSearchParams(new URLSearchParams({ year: String(year), month: String(month), view: "review" }))}
        onPrevMonth={() => {
          const previous = addMonths({ year, month }, -1);
          goToMonth(previous.year, previous.month);
        }}
        onNextMonth={() => {
          const next = addMonths({ year, month }, 1);
          goToMonth(next.year, next.month);
        }}
        onPickMonth={goToMonth}
        onSeeYear={goIndex}
        onMoveSlot={canWriteMonth ? moveCalendarSlot : undefined}
        onAddPost={
          canWriteMonth
            ? async (post: NewPost) => {
                await addSlot.mutateAsync({
                  title: post.title,
                  mediaType: post.mediaType as CalendarMediaType,
                  aspectRatio: post.aspectRatio,
                  orientation: post.orientation as CalendarOrientation,
                  videoLength: post.videoLength as CalendarVideoLength | null,
                  pillarId: post.pillarId,
                  scheduledAt: toUtcIsoDay(year, month, post.day),
                });
              }
            : undefined
        }
        onQuickPost={
          canEdit && !isPastMonth && !generating ? (day) => setQuickPostDay({ year, month, day }) : undefined
        }
        onAddQuickPost={
          canEdit && !isPastMonth && !generating
            ? async (post: NewPost) => {
                // The chat's Quick answer: the same direct-slot mutation as the
                // quick post dialog, then on to the new post for its files.
                const result = await createDirectSlot.mutateAsync({
                  title: post.title,
                  date: toUtcIsoDay(year, month, post.day),
                  mediaType: post.mediaType as CalendarMediaType,
                  aspectRatio: post.aspectRatio,
                  orientation: post.orientation as CalendarOrientation,
                  videoLength: post.mediaType === "VIDEO" ? (post.videoLength as CalendarVideoLength | null) : null,
                  channels: [],
                  slideCount: post.slideCount ?? null,
                });
                if (result.planId === planId) {
                  setSearchParams(
                    new URLSearchParams({
                      year: String(year),
                      month: String(month),
                      view: "concepts",
                      slot: result.slot.id,
                      phase: "publishing",
                    }),
                  );
                }
              }
            : undefined
        }
        onDeleteSlot={
          canWriteMonth
            ? async (id) => {
                await deleteSlot.mutateAsync(id);
              }
            : undefined
        }
        peekAction={{
          canRun: (action, slot) =>
            canWriteMonth &&
            ((action === "send-concept" && sendablePosts([slot], briefs).length > 0) ||
              action === "generate-ideas" ||
              action === "retry-ideas"),
          run: (action, slot) => {
            if (action === "send-concept") setSendingSlotId(slot.id);
            else if (action === "generate-ideas" || action === "retry-ideas") ideation.generateIdeas.mutate(slot.id);
          },
        }}
        menuItems={[
          ...(isGuest && listedPlan
            ? [{ label: "Content review", onPress: () => navigate(contentReviewHref) }]
            : []),
          ...(!isGuest && listedPlan && slots.length > 0
            ? [
                {
                  label: "Client proposals",
                  onPress: () =>
                    setSearchParams(
                      new URLSearchParams({ year: String(year), month: String(month), view: "review" }),
                    ),
                },
              ]
            : []),
          ...(canEdit
            ? [{ label: nobodyToReview ? "Invite guests" : "Guests", onPress: () => setShareOpen(true) }]
            : []),
        ]}
        settings={settingsQuery.data}
        hasCustomCadence={listedPlan?.hasCustomCadence ?? false}
        savingCadence={updateMonthCadence.isPending}
        onSaveCadence={
          canEdit && !generating && planStatus !== "ARCHIVED"
            ? async (draft) => {
                await updateMonthCadence.mutateAsync({ year, month, ...draft });
              }
            : undefined
        }
        onResetCadence={
          canEdit && !generating && planStatus !== "ARCHIVED"
            ? async () => {
                await updateMonthCadence.mutateAsync({ year, month, useBrandCadence: true });
              }
            : undefined
        }
        onReplan={canReplan ? (cadence) => setReplan({ cadence }) : undefined}
        planningSince={plan?.updatedAt ?? listedPlan?.updatedAt ?? null}
        planError={listedPlan?.generationError ?? null}
        onPlan={planThisMonth}
        planPending={generatePlan.isPending}
        onStopPlanning={generating && canEdit && planId ? () => cancelPlanning.mutate() : undefined}
        onRetry={() => void planQuery.refetch()}
        staleSince={planQuery.isError && planQuery.data ? planQuery.dataUpdatedAt : null}
        onDecideRisk={(risk, decision) => decideRisk.mutate({ risk, decision })}
        planId={planId}
        plannedAt={listedPlan?.approvedAt ?? null}
        notices={
          nobodyToReview && !generating ? (
            // Figma 08, "nobody can review December yet": the banner, with Invite guests.
            <div role="status" className="flex flex-wrap items-center gap-4 rounded-panel bg-[var(--co-tonal)] px-5 py-3.5">
              <span className="grid size-9 shrink-0 place-items-center rounded-[4px] bg-co-tonal text-co-text">
                <Users className="size-[17px]" strokeWidth={1.8} aria-hidden="true" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-[16px] font-semibold leading-[22px] text-co-text">
                  Nobody can review {new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-US", { month: "long", timeZone: "UTC" })} yet
                </span>
                <span className="text-[13.5px] leading-5 text-co-text2">
                  {(() => {
                    const sent = slots.filter((slot) => slot.status === "IN_CLIENT_REVIEW").length;
                    const posts = `${slots.length} ${slots.length === 1 ? "post" : "posts"}`;
                    return sent > 0
                      ? `Nobody outside the team can see its ${posts}, so the ${sent} sent to ${selectedBrandName} wait with no one to answer them.`
                      : `Nobody outside the team can see its ${posts}. Invite ${selectedBrandName} before you send.`;
                  })()}
                </span>
              </span>
              {canEdit ? (
                <Button size="small" onPress={() => setShareOpen(true)}>
                  Invite guests
                </Button>
              ) : null}
            </div>
          ) : null
        }
      />
      {modals}
    </div>
  );
}


/**
 * The posts a combined send can take: picked, from the plan, with Creo's brief
 * written (or sent back for changes). A brief still being written holds its
 * post back until it lands.
 */
function sendablePosts(slots: CalendarPlanSlot[], briefs: Map<string, BriefRailInfo>): CalendarPlanSlot[] {
  return slots
    .filter((slot) => slot.origin !== "DIRECT" && slot.status === "SIGNED_OFF")
    .filter((slot) => {
      const status = briefs.get(slot.id)?.status;
      return status === "DRAFT" || status === "CHANGES_REQUESTED" || status === "ACCEPTED";
    })
    .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
}

export default function CalendarExperienceView() {
  const [searchParams] = useSearchParams();
  const year = Number(searchParams.get("year"));
  const month = Number(searchParams.get("month"));
  return validYearMonth(year, month) ? (
    <LiveCalendarDetail year={year} month={month} />
  ) : (
    <LiveCalendarIndex />
  );
}
